import type { WeatherLocation } from "@/lib/weatherLocations";

/**
 * Búsqueda de ciudades (Open-Meteo Geocoding).
 *
 * Por qué este servicio y no el geocoder de OpenWeather que ya usa la función del
 * tiempo: acepta un idioma. Ahí estaba el problema de antes — había que escribir el
 * nombre de la ciudad en el idioma "correcto" o no aparecía. Pasándole el idioma de
 * la app, "Milán" encuentra Milán, "Londres" encuentra Londres y "Siviglia"
 * encuentra Sevilla, y además devuelve el nombre ya traducido para enseñarlo.
 * Los acentos dan igual: "almeria" encuentra Almería.
 *
 * No lleva clave, así que puede llamarse desde el navegador. Lo que devuelve son
 * coordenadas, y ésas son las que guardamos: a partir de ahí el parte del tiempo se
 * pide por lat/lon y ya no hay que volver a adivinar ningún nombre.
 */

const ENDPOINT = "https://geocoding-api.open-meteo.com/v1/search";

/** Idiomas que admite el buscador; el resto cae a inglés. */
const SUPPORTED = new Set([
  "en", "de", "fr", "es", "it", "pt", "ru", "tr", "hi", "ja", "zh", "cs", "nl",
  "pl", "sv", "da", "fi", "no", "el", "ko", "ar",
]);

function apiLanguage(i18nLanguage: string): string {
  const base = (i18nLanguage || "en").split("-")[0].toLowerCase();
  return SUPPORTED.has(base) ? base : "en";
}

type GeoResult = {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  country?: string;
  country_code?: string;
  admin1?: string;
};

export type CitySearchHit = WeatherLocation & {
  /** Región/provincia, para distinguir dos ciudades con el mismo nombre. */
  admin?: string;
  key: string;
};

/**
 * Coordenadas corregidas de entradas CORRUPTAS de la base GeoNames que sirve el
 * geocoding de Open-Meteo. La de La Mojonera (id 2515755) trae el nombre, la
 * población y el CP del municipio del Poniente pero las COORDENADAS del cortijo
 * homónimo de Bacares (Sierra de los Filabres, 1.106 m, 60 km al norte): el
 * widget enseñaba el tiempo de la montaña, 7° más frío (bug real cazado en
 * Ulture Control el 2026-08-16; barrido de los pueblos agrícolas de Almería
 * contra OSM ese día — solo esta entrada estaba mal). Se corrige por id Y por
 * proximidad a las coords malas, para reparar también ciudades guardadas sin id.
 * Se aplica en la BÚSQUEDA y al LEER ciudades guardadas (savedCities): las
 * cuentas y aparatos que ya guardaron la mala se autocorrigen solos.
 */
const GEO_OVERRIDES: Array<{ key: string; bad: { lat: number; lon: number }; good: { lat: number; lon: number } }> = [
  { key: "2515755", bad: { lat: 37.29233, lon: -2.4373 }, good: { lat: 36.785, lon: -2.6964 } }, // La Mojonera
];

/** Repara las coordenadas si la ciudad es una de las corruptas conocidas. */
export function fixCityCoords<T extends WeatherLocation>(loc: T): T {
  const key = (loc as T & { key?: unknown }).key;
  for (const o of GEO_OVERRIDES) {
    const byKey = typeof key === "string" && key === o.key;
    const byCoords = typeof loc.lat === "number" && typeof loc.lon === "number" &&
      Math.abs(loc.lat - o.bad.lat) < 0.02 && Math.abs(loc.lon - o.bad.lon) < 0.02;
    if (!byKey && !byCoords) continue;
    if (loc.lat === o.good.lat && loc.lon === o.good.lon) return loc;
    return { ...loc, lat: o.good.lat, lon: o.good.lon };
  }
  return loc;
}

/**
 * Busca hasta `count` ciudades. `signal` permite cancelar: sin él, la respuesta de
 * "alm" puede llegar después de la de "almeria" y pisar los resultados buenos.
 */
export async function searchCities(
  query: string,
  language: string,
  signal?: AbortSignal,
  count = 6,
): Promise<CitySearchHit[]> {
  const name = query.trim();
  if (name.length < 2) return [];

  const url =
    `${ENDPOINT}?name=${encodeURIComponent(name)}` +
    `&count=${count}&language=${apiLanguage(language)}&format=json`;

  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`City search failed: ${res.status}`);

  const data = (await res.json()) as { results?: GeoResult[] };
  const results = data.results ?? [];

  return results.map((r) => {
    const country = r.country || "";
    const countryCode = (r.country_code || "").toUpperCase();
    return fixCityCoords({
      key: String(r.id),
      city: r.name,
      country,
      countryCode,
      displayName: country ? `${r.name}, ${country}` : r.name,
      lat: r.latitude,
      lon: r.longitude,
      admin: r.admin1,
    });
  });
}

/**
 * ¿Son la misma ciudad? Por coordenadas cuando las hay (tolerancia de ~1 km) y, si
 * no, por nombre + país: las ciudades heredadas del catálogo viejo no tienen
 * coordenadas, y comparando solo por ellas se colarían duplicadas.
 */
export function isSameCity(a: WeatherLocation, b: WeatherLocation): boolean {
  if (a.lat != null && a.lon != null && b.lat != null && b.lon != null) {
    return Math.abs(a.lat - b.lat) < 0.01 && Math.abs(a.lon - b.lon) < 0.01;
  }
  const norm = (s: string) =>
    (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  return norm(a.city) === norm(b.city) && norm(a.country) === norm(b.country);
}
